import { LOCALES, DEFAULT_LOCALE } from '../settings.js';

// Глобальная переменная для переводов
let t = {};

// Функция для загрузки переводов
async function loadTranslations(locale = null) {
  const targetLocale = locale || getCurrentLanguageFromPath();
  
  try {
    const response = await fetch(`/locales/${targetLocale}.json`);
    if (!response.ok) {
      throw new Error(`Failed to load translations for ${targetLocale}`);
    }
    const translations = await response.json();
    t = translations;
    return translations;
  } catch (error) {
    console.error('Error loading translations:', error);
    // Загружаем английский как fallback
    if (targetLocale !== DEFAULT_LOCALE) {
      return loadTranslations(DEFAULT_LOCALE);
    }
    return {};
  }
}

// Получение текущего языка из URL-пути
function getCurrentLanguageFromPath() {
  const path = window.location.pathname;
  
  // Страница blog всегда на английском — не меняем язык
  if (path.includes('/blog')) {
    return DEFAULT_LOCALE;
  }
  
  // Корень (/) = английский
  if (path === '/' || path === '/index') {
    return DEFAULT_LOCALE;
  }
  
  // Страница privacy на английском
  if (path === '/privacy') {
    return DEFAULT_LOCALE;
  }
  
  // Страница terms на английском
  if (path === '/terms') {
    return DEFAULT_LOCALE;
  }
  
  // Страница about на английском
  if (path === '/about') {
    return DEFAULT_LOCALE;
  }
  
  // Страница download на английском
  if (path === '/download') {
    return DEFAULT_LOCALE;
  }
  
  // Страница pricing на английском
  if (path === '/pricing') {
    return DEFAULT_LOCALE;
  }

  // Страница pricing-organizations на английском
  if (path === '/pricing-organizations') {
    return DEFAULT_LOCALE;
  }

  // Проверяем языковые пути (/es/, /br/, /ru/)
  for (const locale of LOCALES) {
    if (locale !== DEFAULT_LOCALE && path.startsWith(`/${locale}`)) {
      return locale;
    }
  }
  
  return DEFAULT_LOCALE;
}

// Конфигурация
const config = {
  current: getCurrentLanguageFromPath(),
  available: LOCALES
};

// Определение языка браузера
function detectBrowserLanguage() {
  const primary = (navigator.language || '').toLowerCase().split('-')[0];
  return primary && config.available.includes(primary) ? primary : DEFAULT_LOCALE;
}

// Получение предпочитаемого языка
function getPreferredLanguage() {
  // Проверяем сохраненный язык в localStorage
  const savedLocale = localStorage.getItem('preferred-locale');
  if (savedLocale && config.available.includes(savedLocale)) {
    return savedLocale;
  }
  
  // Используем язык браузера
  return detectBrowserLanguage();
}

// Переключение языка
function switchLanguage(newLocale) {
  if (!config.available.includes(newLocale)) {
    console.warn(`Unsupported locale: ${newLocale}`);
    return;
  }
  
  if (newLocale === config.current) {
    return; // Язык уже выбран
  }
  
  // Сохраняем выбранный язык
  localStorage.setItem('preferred-locale', newLocale);
  
  // Формируем новый URL
  const currentPath = window.location.pathname;
  const search = window.location.search;
  const hash = window.location.hash;
  
  // Определяем, находимся ли мы на странице privacy, terms, about или download
  const isPrivacyPage = currentPath.includes('privacy');
  const isTermsPage = currentPath.includes('terms');
  const isAboutPage = currentPath.includes('about');
  const isDownloadPage = currentPath.includes('download');
  const isPricingPage = currentPath.includes('pricing');
  const isPricingOrganizationsPage = currentPath.includes('pricing-organizations');

  let newPath;
  if (newLocale === DEFAULT_LOCALE) {
    // Для английского
    if (isPrivacyPage) {
      newPath = '/privacy';
    } else if (isTermsPage) {
      newPath = '/terms';
    } else if (isAboutPage) {
      newPath = '/about';
    } else if (isDownloadPage) {
      newPath = '/download';
    } else if (isPricingPage) {
      newPath = '/pricing';
    } else if (isPricingOrganizationsPage) {
      newPath = '/pricing-organizations';
    } else {
      newPath = '/';
    }
  } else {
    // Для остальных языков
    if (isPrivacyPage) {
      newPath = `/${newLocale}/privacy`;
    } else if (isTermsPage) {
      newPath = `/${newLocale}/terms`;
    } else if (isAboutPage) {
      newPath = `/${newLocale}/about`;
    } else if (isDownloadPage) {
      newPath = `/${newLocale}/download`;
    } else if (isPricingPage) {
      newPath = `/${newLocale}/pricing`;
    } else if (isPricingOrganizationsPage) {
      newPath = `/${newLocale}/pricing-organizations`;
    } else {
      newPath = `/${newLocale}`;
    }
  }
  
  // Перенаправляем
  window.location.href = newPath + search + hash;
}

// Проверка соответствия текущего языка предпочитаемому
function checkLanguageMatch() {
  const preferred = getPreferredLanguage();
  
  if (preferred !== config.current) {
    // Если предпочитаемый язык отличается от текущего, переключаемся
    switchLanguage(preferred);
  }
}

// Инициализация
async function initI18n() {
  // Проверяем соответствие языка только при первой загрузке
  // и только на главной странице
  const currentPath = window.location.pathname;
  if (currentPath === '/' || currentPath === '/index') {
    checkLanguageMatch();
  }
}

// Инициализация при загрузке страницы
document.addEventListener('DOMContentLoaded', initI18n);

// Экспортируем переменную t и функцию загрузки переводов
export { switchLanguage, t, loadTranslations, getCurrentLanguageFromPath }; 